import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  MessageCircle, Users, Circle, Send, X, Clock,
  Monitor, Smartphone, ArrowRight, Inbox, Zap
} from 'lucide-react';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAGE_LABELS = {
  '/': 'Accueil',
  '/catalogue': 'Catalogue',
  '/creer-evenement': 'Configurateur',
  '/panier': 'Panier',
  '/partenaires': 'Partenaires',
  '/contact': 'Contact',
  '/connexion': 'Connexion',
};
const getPageLabel = (path) => {
  if (!path) return 'Site';
  for (const [k, v] of Object.entries(PAGE_LABELS)) {
    if (path === k) return v;
  }
  if (path.startsWith('/produit/')) return 'Fiche produit';
  if (path.startsWith('/devis/')) return 'Page devis';
  if (path.startsWith('/mon-evenement')) return 'Mon événement';
  if (path.startsWith('/mes-evenements')) return 'Mes événements';
  return path.replace('/', '') || 'Site';
};

function timeSince(isoStr) {
  if (!isoStr) return '';
  const secs = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}min`;
  return `${Math.floor(secs / 3600)}h`;
}

export default function AdminLiveChat() {
  const [visitors, setVisitors] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [tab, setTab] = useState('live');
  const [stats, setStats] = useState({ online: 0, open_threads: 0, unread: 0 });
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Stats polling
  useEffect(() => {
    fetchStats();
    fetchThreads();
    const interval = setInterval(() => { fetchStats(); fetchThreads(); }, 15000);
    return () => clearInterval(interval);
  }, []);

  async function fetchStats() {
    try {
      const r = await fetch(`${BACKEND}/api/livechat/stats`, { headers: authHeaders() });
      const d = await r.json();
      setStats(d);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  async function fetchThreads() {
    try {
      const r = await fetch(`${BACKEND}/api/livechat/threads`, { headers: authHeaders() });
      const d = await r.json();
      setThreads(d.threads || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  async function openThread(thread) {
    setActiveThread(thread);
    try {
      const r = await fetch(`${BACKEND}/api/livechat/threads/${thread.thread_id}/messages`, { headers: authHeaders() });
      const d = await r.json();
      setMessages(d.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  // WebSocket admin
  useEffect(() => {
    const wsUrl = BACKEND
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/admin`);
    wsRef.current = ws;

    ws.onopen = () => { if (process.env.NODE_ENV !== 'production') console.log('[Admin WS] Connected'); };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'init') {
          setVisitors(data.visitors || []);
        } else if (data.type === 'visitor_connected') {
          setVisitors(prev => {
            const filtered = prev.filter(v => v.visitor_id !== data.visitor_id);
            return [...filtered, data];
          });
          toast.info(`Nouveau visiteur sur ${getPageLabel(data.current_page)}`, {
            description: data.user_name !== 'Visiteur anonyme' ? `Client : ${data.user_name}` : undefined,
            duration: 5000,
          });
          setStats(s => ({ ...s, online: s.online + 1 }));
        } else if (data.type === 'visitor_disconnected') {
          setVisitors(prev => prev.filter(v => v.visitor_id !== data.visitor_id));
          setStats(s => ({ ...s, online: Math.max(0, s.online - 1) }));
        } else if (data.type === 'visitor_page_change') {
          setVisitors(prev => prev.map(v =>
            v.visitor_id === data.visitor_id ? { ...v, current_page: data.page } : v
          ));
        } else if (data.type === 'new_message') {
          fetchThreads();
          setStats(s => ({ ...s, unread: s.unread + 1 }));
          toast.message(`💬 Message de ${data.user_name}`, {
            description: data.body.slice(0, 80),
            action: { label: 'Répondre', onClick: () => { setTab('threads'); } },
            duration: 8000,
          });
          // Si la conversation est active, ajouter le message
          if (activeThread?.visitor_id === data.visitor_id) {
            setMessages(prev => [...prev, {
              sender: 'visitor', body: data.body, created_at: data.created_at
            }]);
          }
        }
      } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    };
    ws.onerror = () => { if (process.env.NODE_ENV !== 'production') console.warn('[Admin WS] Error'); };
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
    }, 25000);
    return () => { clearInterval(ping); ws.close(); };
  // eslint-disable-next-line
  }, []);

  function sendMessage() {
    if (!input.trim() || !activeThread) return;
    const body = input.trim();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        thread_id: activeThread.thread_id,
        visitor_id: activeThread.visitor_id,
        body,
      }));
      setMessages(prev => [...prev, {
        sender: 'admin', body, created_at: new Date().toISOString()
      }]);
      setInput('');
    } else {
      toast.error('WebSocket déconnecté. Rechargez la page.');
    }
  }

  function startChatWithVisitor(visitor) {
    // Chercher un thread existant ou créer via WS
    const existing = threads.find(t => t.visitor_id === visitor.visitor_id);
    if (existing) {
      setTab('threads');
      openThread(existing);
    } else {
      // Initier via WebSocket (le visiteur aura un thread créé automatiquement)
      toast.info(`Initiez une conversation : envoyez un message via le formulaire ci-dessous.`);
      setActiveThread({
        thread_id: null,
        visitor_id: visitor.visitor_id,
        user_name: visitor.user_name,
        current_page: visitor.current_page,
      });
      setTab('threads');
      setMessages([]);
    }
  }

  const timeLabel = (iso) => {
    try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Live Chat</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Conversations en temps réel avec les visiteurs</p>
      </div>
      <div data-testid="admin-livechat" style={{ display: 'flex', height: 'calc(100vh - 160px)', gap: 16, minHeight: 0 }}>

      {/* PANNEAU GAUCHE */}
      <div style={{ width: 300, display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0 }}>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[
            { icon: <Circle size={12} />, value: stats.online, label: 'En ligne', color: '#3DDC97' },
            { icon: <MessageCircle size={12} />, value: stats.open_threads, label: 'Convos', color: 'var(--admin-teal)' },
            { icon: <Zap size={12} />, value: stats.unread, label: 'Non lus', color: 'var(--admin-gold)' },
          ].map((k, i) => (
            <div key={`msg-${i}`} style={{ background: 'var(--admin-surface)', border: `1px solid ${k.color}25`, borderRadius: 14, padding: '10px 12px', textAlign: 'center' }}>
              <p style={{ fontSize: 20, fontWeight: 700, color: k.color, margin: 0 }}>{k.value}</p>
              <p style={{ fontSize: 10, color: 'var(--admin-muted)', margin: 0 }}>{k.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--admin-surface)', borderRadius: 12, padding: 4 }}>
          {[
            { id: 'live', label: 'Visiteurs live', icon: <Users size={13} /> },
            { id: 'threads', label: 'Conversations', icon: <Inbox size={13} /> },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: '7px 8px', borderRadius: 9, fontSize: 11, fontWeight: 600,
                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center',
                background: tab === t.id ? '#2A2A2A' : 'transparent',
                color: tab === t.id ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
                transition: 'all 0.15s',
              }}
            >{t.icon} {t.label}</button>
          ))}
        </div>

        {/* Liste visiteurs live */}
        {tab === 'live' && (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visitors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <Users size={32} style={{ color: 'rgba(255,255,255,0.1)', marginBottom: 8 }} />
                <p style={{ color: 'var(--admin-faint)', fontSize: 12 }}>Aucun visiteur en ligne</p>
              </div>
            ) : visitors.map(v => (
              <div key={v.visitor_id}
                style={{
                  background: 'var(--admin-surface)', border: '1px solid #2A2A2A', borderRadius: 14,
                  padding: '12px 14px', cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#5DBCBF40'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2A2A2A'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#3DDC97', flexShrink: 0,
                    boxShadow: '0 0 6px #3DDC97',
                  }} />
                  <p style={{ color: 'var(--admin-text)', fontSize: 13, fontWeight: 600, margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.user_name || 'Visiteur anonyme'}
                  </p>
                  <span style={{ fontSize: 10, color: 'var(--admin-faint)' }}>{timeSince(v.connected_at)}</span>
                </div>
                <p style={{ fontSize: 11, color: 'var(--admin-teal)', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Monitor size={10} /> {getPageLabel(v.current_page)}
                </p>
                <button
                  onClick={() => startChatWithVisitor(v)}
                  style={{
                    width: '100%', padding: '6px 0', borderRadius: 9, fontSize: 11, fontWeight: 600,
                    background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)', border: '1px solid var(--admin-teal-line)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}
                >
                  <MessageCircle size={12} /> Ouvrir le chat
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Liste conversations */}
        {tab === 'threads' && (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {threads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <Inbox size={32} style={{ color: 'rgba(255,255,255,0.1)', marginBottom: 8 }} />
                <p style={{ color: 'var(--admin-faint)', fontSize: 12 }}>Aucune conversation</p>
              </div>
            ) : threads.map(t => (
              <div key={t.thread_id}
                onClick={() => openThread(t)}
                style={{
                  background: activeThread?.thread_id === t.thread_id ? '#1A1A2E' : '#161616',
                  border: `1px solid ${activeThread?.thread_id === t.thread_id ? '#5DBCBF40' : t.unread_admin ? '#D4AF3730' : '#2A2A2A'}`,
                  borderRadius: 14, padding: '11px 13px', cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  {t.unread_admin && (
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#D4AF37', flexShrink: 0 }} />
                  )}
                  <p style={{ color: 'var(--admin-text)', fontSize: 12, fontWeight: 600, margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.visitor_id?.slice(0, 12)}…
                  </p>
                  <span style={{ fontSize: 10, color: 'var(--admin-faint)' }}>
                    {t.last_message_at ? timeSince(t.last_message_at) : ''}
                  </span>
                </div>
                {t.last_message && (
                  <p style={{ fontSize: 11, color: 'var(--admin-muted)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.last_message.sender === 'admin' ? '↑ ' : '↓ '}{t.last_message.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PANNEAU DROIT : conversation active */}
      <div style={{
        flex: 1, background: 'var(--admin-bg)', borderRadius: 18,
        border: '1px solid #2A2A2A', display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0,
      }}>
        {!activeThread ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <MessageCircle size={48} style={{ color: 'rgba(255,255,255,0.08)' }} />
            <p style={{ color: 'var(--admin-faint)', fontSize: 14 }}>Sélectionnez un visiteur ou une conversation</p>
            <p style={{ color: 'var(--admin-faint)', fontSize: 12 }}>{stats.online} visiteur(s) en ligne actuellement</p>
          </div>
        ) : (
          <>
            {/* Header conversation */}
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #2A2A2A', background: 'var(--admin-surface)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3DDC97', boxShadow: '0 0 6px #3DDC97' }} />
              <div style={{ flex: 1 }}>
                <p style={{ color: 'var(--admin-text)', fontSize: 14, fontWeight: 600, margin: 0 }}>
                  {activeThread.user_name || `Visiteur ${activeThread.visitor_id?.slice(0, 8)}`}
                </p>
                {activeThread.current_page && (
                  <p style={{ color: 'var(--admin-teal)', fontSize: 11, margin: 0 }}>
                    Sur : {getPageLabel(activeThread.current_page)}
                  </p>
                )}
              </div>
              <button onClick={() => setActiveThread(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-faint)' }}>
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.length === 0 && (
                <p style={{ color: 'var(--admin-faint)', fontSize: 12, textAlign: 'center', marginTop: 32 }}>
                  Démarrez la conversation !
                </p>
              )}
              {messages.map((msg, i) => (
                <div key={`msg-${i}`} style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: msg.sender === 'admin' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '72%', padding: '9px 14px',
                    borderRadius: msg.sender === 'admin' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.sender === 'admin'
                      ? 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)'
                      : '#1C1C1C',
                    color: msg.sender === 'admin' ? '#FFFFFF' : 'rgba(255,255,255,0.9)',
                    fontSize: 13, lineHeight: 1.5,
                  }}>
                    {msg.body}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--admin-faint)', marginTop: 3 }}>
                    {msg.sender === 'admin' ? 'Vous' : 'Visiteur'} · {timeLabel(msg.created_at)}
                  </span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input réponse */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #2A2A2A', display: 'flex', gap: 10 }}>
              <input
                type="text"
                placeholder="Répondre au visiteur…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 12, fontSize: 13,
                  background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)', outline: 'none',
                }}
              />
              <button onClick={sendMessage}
                style={{
                  padding: '10px 18px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                  background: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)',
                  color: 'var(--admin-text)', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Send size={14} /> Envoyer
              </button>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
